/**
 * /////////////////////////////////////////////////////////////////////////////
 *
 * @Copyright (C) 2025 Kruczek Labs LLC
 *
 * KeepTrack is free software: you can redistribute it and/or modify it under the
 * terms of the GNU Affero General Public License as published by the Free Software
 * Foundation, either version 3 of the License, or (at your option) any later version.
 *
 * KeepTrack is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License along with
 * KeepTrack. If not, see <http://www.gnu.org/licenses/>.
 *
 * /////////////////////////////////////////////////////////////////////////////
 */

import type {
  AtmosphereSettings,
  EarthBumpTextureQuality,
  EarthCloudTextureQuality,
  EarthDayTextureQuality,
  EarthNightTextureQuality,
  EarthPoliticalTextureQuality,
  EarthSpecTextureQuality,
  EarthTextureStyle,
} from '@app/engine/rendering/draw-manager/earth-quality-enums';
import type { MilkyWayTextureQuality } from '@app/engine/rendering/draw-manager/skybox-sphere';
import type { SunTextureQuality } from '@app/engine/rendering/draw-manager/sun';

/**
 * Graphics and rendering settings
 */
export class GraphicsSettings {
  // Earth Texture Quality Settings
  earthDayTextureQuality: EarthDayTextureQuality;
  earthNightTextureQuality: EarthNightTextureQuality;
  earthSpecTextureQuality: EarthSpecTextureQuality;
  earthBumpTextureQuality: EarthBumpTextureQuality;
  earthCloudTextureQuality: EarthCloudTextureQuality;
  /**
   * @deprecated Political borders are now rasterized from GeoJSON with automatic
   * camera-distance LOD; this quality setting is ignored. Kept so saved
   * settings/URLs referencing it don't break.
   */
  earthPoliticalTextureQuality: EarthPoliticalTextureQuality;

  // Earth Texture Style
  earthTextureStyle: EarthTextureStyle;
  isEarthGrayScale = false;
  isEarthAmbientLighting = true;
  isDrawPoliticalMap = true;
  /** Country name labels at Natural Earth label points (independent of the border overlay). */
  isDrawPoliticalLabels = false;
  isDrawCloudsMap = true;

  // Earth Rendering
  /**
   * If false, skip drawing the Earth mesh entirely (debug only).
   */
  isDrawEarth = true;
  /**
   * If true, hide the earth textures and make the globe black
   */
  isBlackEarth = false;
  /**
   * Determines whether or not to load the specularity map for the Earth.
   */
  isDrawSpecMap = true;
  /**
   * Determines whether or not to load the bump map for the Earth.
   */
  isDrawBumpMap = true;
  /**
   * The number of latitude segments used to render the Earth object.
   */
  earthNumLatSegs = 256;
  /**
   * The number of longitude segments used to render the Earth.
   */
  earthNumLonSegs = 256;
  /**
   * Gain applied to the Earth's night (city-lights) texture on the unlit hemisphere
   * (1 = stock brightness, >1 brightens). Lifts the dark limb so it's readable on dim
   * mobile screens without touching the day side. The companion profile raises it.
   */
  earthNightBrightness = 1;
  /**
   * Draws a soft, smoothly-pulsing glow around the selected satellite in non-ride-along
   * views (mirrors the 2D companion map's live marker). Off by default so the stock
   * keeptrack.space look is unchanged; the companion profile turns it on.
   */
  isDrawSelectionGlow = false;
  /**
   * "You are here" observer marker. When set to a { lat, lon } in DEGREES the globe draws
   * a green ground marker there (depth-occluded on the far side); null draws nothing.
   * Data-driven — the companion feeds the device GPS position through the embed bridge.
   */
  observerMarkerLla: { lat: number; lon: number } | null = null;

  // Atmosphere and Aurora
  /**
   * Determines whether the atmosphere should be drawn or not.
   * 0 = Off
   * 1 = On
   */
  isDrawAtmosphere: AtmosphereSettings = 1;
  /**
   * Determines whether or not to draw the Aurora effect.
   */
  isDrawAurora = true;
  /**
   * Determines whether or not to draw graticule (lat/lon grid) lines on the Earth.
   */
  isDrawGraticule = false;
  /**
   * Determines whether or not to draw the day/night terminator line and subsolar
   * point marker on the flat (equirectangular) map view.
   */
  isDrawFlatMapTerminator = true;

  // Sun Settings
  sunTextureQuality: SunTextureQuality;
  /**
   * Determines whether or not to draw the sun in the application.
   */
  isDrawSun = true;
  /**
   * The size of the sun in the simulation, represented as a scale factor.
   * A value of 0.9 indicates the sun is displayed at 90% of its default size.
   * A value of 1.1 indicates the sun is displayed at 110% of its default size.
   */
  sizeOfSun = 1.1;
  /**
   * Determines whether to use a sun texture.
   * When set to true, the application will render the sun with a custom texture.
   * When set to false, the application will use a default sun representation.
   * @default false
   */
  isUseSunTexture = false;
  isDrawNightAsDay = false;
  /**
   * When true, skips per-frame interpolation of TLE satellite positions.
   * Satellites will only update when a new propagation message arrives from the worker.
   */
  isSkipTleInterpolation = false;

  /**
   * Disable the runtime auto-quality-downgrade loop (Scene.updateVisualsBasedOnPerformance_),
   * which disables visual features one-by-one and shows a "Your computer is struggling!" toast
   * when sustained FPS is below the limit. Off by default so real low-end devices still scale
   * down; E2E sets it true because the headless/SwiftShader GPU is always below the limit and
   * the resulting toast stack overlaps and blocks side-menu close buttons.
   */
  isDisablePerformanceDowngrade = false;

  // Godrays (Sun illumination effects)
  /**
   * Disable drawing godrays (huge performance hit on mobile)
   */
  isDisableGodrays = false;
  /**
   * Determines how many draw commands are used for sun illumination
   * This should be a GodraySamples value (24, 36, 52, 80)
   * @default 24
   */
  godraysSamples = 24;
  /**
   * The decay factor for the godray effect.
   *
   * This value controls how fast the godray intensity decreases with distance.
   * Lower values result in shorter/less visible godrays, while higher values
   * create longer/more prominent godrays.
   *
   * @default 0.983
   */
  godraysDecay = 0.983;
  /**
   * The exposure level for the godrays effect.
   * Controls the brightness/intensity of the godray rendering.
   * Higher values make godrays more pronounced.
   * @default 0.75.
   */
  godraysExposure = 0.4;
  /**
   * The density of godrays effect.
   * Controls the intensity and thickness of the light scattering effect.
   * Higher values result in more pronounced godrays.
   * @default 1.8
   */
  godraysDensity = 1.8;
  /**
   * The weight factor for the godray effect.
   * Controls the intensity of the godray/light scattering effect.
   * Higher values increase the visibility of godrays.
   * @default 0.085
   */
  godraysWeight = 0.085;
  /**
   * Represents the rate at which the intensity of godrays (volumetric light scattering) diminishes
   * with distance from the light source.
   *
   * Higher values make the godrays fade more quickly as they extend away from the light source.
   * Lower values allow the godrays to extend further with less intensity reduction.
   *
   * @default 2.7
   */
  godraysIlluminationDecay = 2.7;

  // Skybox and Space
  /**
   * Far clipping plane distance in km. The default (1e12 km ≈ 6700 AU) covers the
   * full heliocentric view + star field. Deployments that never leave Earth orbit
   * (e.g. the companion embed) should set this to ~2e5 km: even with the
   * logarithmic depth buffer, a tighter window buys depth precision and avoids
   * z-artifacts on close geometry.
   */
  zFar = 1e12;
  /**
   * Backing-store scale for the WebGL canvas. 1 (default) renders at CSS pixels —
   * the historical behavior, which on high-DPR phones means rendering at 1/2–1/3
   * of native resolution and upscaling (visible aliasing). 0 = automatic:
   * min(window.devicePixelRatio, 2), the mobile sweet spot. Values > 1 are used
   * as-is (clamped to 3). Fill-rate cost scales with the square of this value.
   */
  canvasPixelRatio = 1;
  /**
   * WebGL context `preserveDrawingBuffer` attribute (read once at context
   * creation). True (default) lets the Screenshot/ScreenRecorder plugins read the
   * canvas outside the render callback, but forces the compositor to copy —
   * rather than swap — the drawing buffer every frame and blocks tile-memory
   * discard on mobile GPUs. Profiles that ship no canvas-capture UI (e.g. the
   * companion embed) should set this false for a per-frame bandwidth win.
   */
  isPreserveDrawingBuffer = true;
  /**
   * Determines whether the Milky Way should be drawn on the screen.
   */
  isDrawMilkyWay = true;
  /**
   * Default resolution for Milky Way texture
   */
  milkyWayTextureQuality: MilkyWayTextureQuality;
  /**
   * Use 16K textures for the Milky Way
   */
  hiresMilkWay = false;
  /**
   * Determines whether the background of the canvas should be gray or black.
   *
   * NOTE: This is only used when the Milky Way is not drawn.
   */
  isGraySkybox = false;
  isDisableSkybox = false;
  isDisablePlanets = false;
  /**
   * Draw the asteroid belt, the Hilda group and Jupiter's Trojan swarms as a procedural
   * point cloud. Only visible once the camera is out at interplanetary range (see
   * AsteroidBelt), and suppressed entirely whenever `isDisablePlanets` is set.
   */
  isDrawAsteroidBelt = true;
  /**
   * How many bodies the asteroid belt generates. The whole population is one GPU draw with
   * the orbit solve in the vertex shader, so the cost is essentially fill rate: raising this
   * makes the belt dustier and more expensive when it fills the screen, lowering it makes the
   * Kirkwood gaps and the Hilda triangle harder to read.
   */
  asteroidBeltObjectCount = 15000;

  // Stars and Constellations
  /**
   * TODO: Reimplement stars
   */
  isDisableStars = true;

  // Image Quality
  /**
   * Determines whether small images should be used.
   *
   * Use these to default smallest resolution maps
   * Really useful on small screens and for faster loading times
   */
  smallImages = false;

  // Satellite Shader Settings
  /**
   * The settings for the satellite shader.
   */
  satShader = {
    /**
     * The minimum zoom level at which large objects are displayed.
     */
    largeObjectMinZoom: 0.37,
    /**
     * The maximum zoom level at which large objects are displayed.
     */
    largeObjectMaxZoom: 0.58,
    /**
     * The minimum size of objects in the shader.
     */
    minSize: 5.5,
    /**
     * The maximum size of objects in the shader.
     */
    maxSize: 48.0,
    /**
     * The minimum size of objects in the shader when in planetarium mode.
     */
    minSizePlanetarium: 20.0,
    /**
     * The maximum size of objects in the shader when in planetarium mode.
     */
    maxSizePlanetarium: 20.0,
    starMinSize: 8.0,
    /**
     * The maximum allowed size of objects in the shader.
     * This value is dynamically changed based on zoom level.
     */
    maxAllowedSize: 35.0,
    /**
     * Whether or not to use dynamic sizing for objects in the shader.
     */
    isUseDynamicSizing: false,
    /**
     * The scalar value used for dynamic sizing of objects in the shader.
     */
    dynamicSizeScalar: 1.0,
    /**
     * The size of stars and searched objects in the shader.
     */
    starSize: '16.0',
    /**
     * The distance at which objects start to grow in kilometers.
     * Must be a float as a string for the GPU to read.
     * This makes stars bigger than satellites.
     */
    distanceBeforeGrow: '14000.0',
    /**
     * The blur radius factor used for satellites.
     */
    blurFactor1: '0.53',
    /**
     * The blur alpha factor used for satellites.
     */
    blurFactor2: '0.5',
    /**
     * The blur radius factor used for stars.
     */
    blurFactor3: '0.43',
    /**
     * The blur alpha factor used for stars.
     */
    blurFactor4: '0.25',
    /**
     * Fraction of the camera-to-dot range each dot is pulled toward the camera
     * (along the view ray) before projection. Prevents the Earth sphere from
     * slicing through the screen-facing point sprite when a dot sits on or
     * just above the surface. Ray-aligned, so there is no parallax shift.
     * Must be a float as a string for the GPU to read.
     */
    depthPullFactor: '0.02',
    /**
     * Hard cap (in km) on the camera-pull distance above. Because the pull is a
     * fraction of the camera-to-dot range, zooming out makes an uncapped pull
     * grow without bound and drag back-of-Earth dots forward until they bleed
     * through the limb. The cap only needs to exceed the Earth sphere's faceting
     * sagitta (~1.9 km at 128 segments), so 5 km always defeats slicing while
     * keeping the far-zoom pull far too small to expose the back side.
     * Must be a float as a string for the GPU to read.
     */
    depthPullMaxKm: '5.0',
    /**
     * Rendering style for the core satellite dot (DotStyle enum):
     * 0 soft glow (legacy) | 1 solid disc | 2 ring | 3 diamond | 4 square.
     * Applied live via the u_dotStyle uniform - no shader rebuild needed.
     */
    dotStyle: 0,
    /**
     * Draw identification markers around special dots: thin ring for
     * search/group members, reticle for the selected object, halo for the
     * hovered object. When false, those dots render as plain big dots
     * (legacy behavior).
     */
    isStatusMarkers: true,
  };

  /**
   * Size of the dot vertex shader
   */
  vertShadersSize = 12;
  /**
   * Size of the dot for picking purposes
   */
  pickingDotSize: string = '18.0';

  // Mesh Settings
  /**
   * Which mesh to use if meshOverride is set
   */
  meshOverride: string | null = null;
  /**
   * The rotation of the mesh if meshOverride is set
   */
  meshRotation = {
    x: 0,
    y: 0,
    z: 0,
  };
  /**
   * Add custom mesh list to force loading of specific meshes
   *
   * These can then be used in the mesh manager to force a specific mesh to be used
   */
  meshListOverride: string[] = [];
  /**
   * Override the default models on satellite view
   */
  modelsOnSatelliteViewOverride = false;
  noMeshManager = false;

  // Advanced Graphics
  isDisableAsyncReadPixels = false;

  // Debug
  debugMobilePicking = false;
}

export const defaultGraphicsSettings = new GraphicsSettings();
